/**
 * Which Settings tabs a role may open that the server also guards (decision 5, approved by
 * Andrew 2026-10-08; SOC 2 CC6.3: what the console shows must match what the server allows).
 *
 * ONE source for the console and the server. The server cannot import this package (its
 * build root is src/), so src/aithora/settings-access.ts is a byte-for-byte copy and a unit
 * test (settings-tab-parity.test.ts) fails when the two differ, or when the console's tab
 * list and the server's gate disagree for any role in any environment.
 *
 * Atlas Assistant: Super Admin and Operator (the owner is a Super Admin) everywhere. A Developer only
 * outside production (the review environment and local development): in production a
 * Developer changes Atlas through reviewed code and the deploy pipeline, never live toggles
 * (SOC 2 CC8.1), so the tab is hidden and a direct link says NO_SETTING_ACCESS.
 */

/** Where the server runs. Unknown counts as production (the stricter answer). */
export type DeployEnv = 'production' | 'review' | 'development';

export function toDeployEnv(raw: unknown): DeployEnv {
  return raw === 'review' || raw === 'development' ? raw : 'production';
}

/** The settings tabs this file rules on. */
export type GuardedSettingsTab = 'Atlas Assistant';

const ALWAYS: Record<GuardedSettingsTab, readonly string[]> = {
  // OWNER is never a stored role (the owner is a Super Admin, known by email), so it is not here.
  'Atlas Assistant': ['SUPER_ADMIN', 'OPERATOR'],
};

const OUTSIDE_PRODUCTION: Record<GuardedSettingsTab, readonly string[]> = {
  'Atlas Assistant': ['DEVELOPER'],
};

/** May this stored role open this settings tab in this environment? Unknown roles may not. */
export function mayOpenSettingsTab(role: string | null | undefined, tab: GuardedSettingsTab, env: DeployEnv): boolean {
  const r = String(role ?? '').trim().toUpperCase();
  if (!r) return false;
  if (ALWAYS[tab].includes(r)) return true;
  return env !== 'production' && OUTSIDE_PRODUCTION[tab].includes(r);
}

/** What a direct link to a tab the role may not open shows. */
export const NO_SETTING_ACCESS = "You don't have access to this setting";
